import { useEffect, useState } from 'react';
import { calculateLevel, type PublicPlayerProfile } from '@big2/shared';
import { useGameState } from '../../net/GameProvider.js';
import { useI18n } from '../../i18n.js';
import { getUserAvatar } from '../../utils/avatar.js';
import { Icon } from '../Icon.js';
import './playerProfileModal.css';

export interface PlayerProfileModalProps {
  userId: string;
  initialPlayerName?: string;
  onClose: () => void;
  tableInteractionPlayer?: {
    playerId: string;
    name: string;
    seat: number;
    tier?: string;
  } | null;
  onOpenTableInteractions?: (player: { playerId: string; name: string; seat: number; tier?: string }) => void;
}

export function PlayerProfileModal({
  userId,
  initialPlayerName,
  onClose,
  tableInteractionPlayer,
  onOpenTableInteractions,
}: PlayerProfileModalProps) {
  const { state, actions } = useGameState();
  const { lang } = useI18n();
  const isMn = lang === 'mn';

  const [activeTab, setActiveTab] = useState<'overview' | 'history'>('overview');
  const [friendPendingLocal, setFriendPendingLocal] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    setFriendPendingLocal(false);
    actions.inspectPlayer(userId);
    return () => {
      actions.clearInspectedPlayer();
    };
  }, [userId]);

  useEffect(() => {
    if (state.error) {
      setFriendPendingLocal(false);
    }
  }, [state.error]);

  const p: PublicPlayerProfile | null =
    state.inspectedPlayer && state.inspectedPlayer.id === userId
      ? state.inspectedPlayer
      : null;

  const displayName = p?.displayName || initialPlayerName || (isMn ? 'Тоглогч' : 'Player');
  const avatar = getUserAvatar(userId, displayName);
  const xp = p?.xp ?? 0;
  const levelInfo = calculateLevel(xp, isMn ? 'mn' : 'en');
  const isPaid = p?.tier === 'paid';
  const gamesPlayed = p?.gamesPlayed ?? 0;
  const wins = p?.wins ?? 0;
  const winRate = p ? p.winRate : gamesPlayed > 0 ? Math.round((wins / gamesPlayed) * 100) : 0;

  const isSelf = state.profile?.id === userId;
  const isFriend = Boolean(
    p?.isFriend ||
    (state.profile && state.friendsData?.friends.some((f) => f.userId === userId))
  );
  const isPending = Boolean(
    friendPendingLocal ||
    p?.isPending ||
    (state.profile && state.friendsData?.outgoingRequests.some((r) => r.toUserId === userId))
  );

  const handleAddFriend = () => {
    if (!state.profile) {
      setNotice(isMn ? 'Найз нэмэхийн тулд нэвтэрнэ үү.' : 'Please sign in to add friends.');
      return;
    }
    setFriendPendingLocal(true);
    setNotice(null);
    actions.sendFriendRequest({ toUserId: userId });
  };

  const handleRemoveFriend = () => {
    const confirmMsg = isMn
      ? `${displayName}-г найзын жагсаалтаас хасах уу?`
      : `Remove ${displayName} from your friends list?`;
    if (window.confirm(confirmMsg)) {
      actions.removeFriend(userId);
    }
  };

  const formatRelativeTime = (iso: string) => {
    try {
      const diffMs = Date.now() - Date.parse(iso);
      const mins = Math.floor(diffMs / 60000);
      if (mins < 1) return isMn ? 'Дөнгөж сая' : 'Just now';
      if (mins < 60) return isMn ? `${mins} минутын өмнө` : `${mins}m ago`;
      const hours = Math.floor(mins / 60);
      if (hours < 24) return isMn ? `${hours} цагийн өмнө` : `${hours}h ago`;
      const days = Math.floor(hours / 24);
      return isMn ? `${days} өдрийн өмнө` : `${days}d ago`;
    } catch {
      return '';
    }
  };

  const formatGameTypeName = (gameType: string) => {
    switch (gameType) {
      case 'muushig':
        return isMn ? '♣ Муушиг' : '♣ Muushig';
      case 'twentyone':
        return isMn ? '♦ 21' : '♦ 21';
      case 'daaluu':
        return isMn ? '🂍 Даалуу' : '🂍 Daaluu';
      default:
        return isMn ? '♠ Арван гурав' : '♠ Big Two';
    }
  };

  return (
    <div
      className="player-profile-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={isMn ? `${displayName} тоглогчийн профайл` : `${displayName}'s Profile`}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div className="player-profile-card" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button
          type="button"
          className="player-profile-close-btn"
          onClick={onClose}
          aria-label={isMn ? 'Хаах' : 'Close'}
        >
          <Icon name="x" size={18} />
        </button>

        {/* Hero Section */}
        <div className="player-profile-hero">
          <div
            className={`player-profile-avatar${isPaid ? ' is-vip' : ''}`}
            style={{
              background: avatar.gradient,
              borderColor: avatar.borderColor,
              boxShadow: `0 0 16px ${avatar.glow}`,
            }}
          >
            {avatar.initial}
            {isPaid && (
              <span className="player-profile-vip-crown" title="VIP Member">
                <Icon name="crown" size={14} />
              </span>
            )}
          </div>

          <div className="player-profile-hero-meta">
            <div className="player-profile-name-row">
              <h3 className="player-profile-name">{displayName}</h3>
              {isPaid && <span className="player-profile-vip-tag">VIP</span>}
            </div>

            <div className="player-profile-rank-row">
              <span
                className="player-profile-level-badge"
                style={{
                  background: `${levelInfo.tierColor}22`,
                  borderColor: levelInfo.tierColor,
                  color: levelInfo.tierColor,
                }}
              >
                {levelInfo.rankBadge} {isMn ? `Түвшин ${levelInfo.level}` : `Lv.${levelInfo.level}`}
              </span>
              <span className="player-profile-rank-title">{levelInfo.rankTitle}</span>
            </div>

            {/* XP Progress Bar */}
            <div className="player-profile-xp-bar-container">
              <div
                className="player-profile-xp-bar-fill"
                style={{
                  width: `${levelInfo.progressPercent}%`,
                  backgroundColor: levelInfo.tierColor,
                }}
              />
            </div>
            <div className="player-profile-xp-labels">
              <span>{levelInfo.currentLevelXp} XP</span>
              <span>{levelInfo.nextLevelXp} XP</span>
            </div>
          </div>
        </div>

        {/* Quick Social & Action Bar */}
        <div className="player-profile-actions-bar">
          {/* Friendship Status / Button */}
          {isSelf ? (
            <span className="player-profile-self-tag">{isMn ? '👤 Та өөрөө' : '👤 You'}</span>
          ) : isFriend ? (
            <div className="player-profile-friend-chip">
              <span className="player-profile-friend-label">✓ {isMn ? 'Найз' : 'Friend'}</span>
              <button
                type="button"
                className="player-profile-unfriend-btn"
                onClick={handleRemoveFriend}
                title={isMn ? 'Найзаас хасах' : 'Remove friend'}
              >
                ✕
              </button>
            </div>
          ) : isPending ? (
            <span className="player-profile-pending-tag">
              ⏳ {isMn ? 'Хүсэлт илгээсэн' : 'Request Pending'}
            </span>
          ) : (
            <button
              type="button"
              className="player-profile-add-friend-btn"
              onClick={handleAddFriend}
            >
              ➕ {isMn ? 'Найзаар нэмэх' : 'Add Friend'}
            </button>
          )}

          {notice && (
            <div style={{ color: '#f87171', fontSize: '0.82em', textAlign: 'center', margin: '4px 0' }}>
              {notice}
            </div>
          )}

          {/* Table Interaction Shortcut (Throw Item / Taunt) */}
          {tableInteractionPlayer && onOpenTableInteractions && (
            <button
              type="button"
              className="player-profile-taunt-btn"
              onClick={() => {
                onClose();
                onOpenTableInteractions(tableInteractionPlayer);
              }}
              title={isMn ? 'Эд зүйл шидэх / Тавлах' : 'Throw item or taunt player'}
            >
              🎯 {isMn ? 'Шидэх / Тавлах' : 'Throw / Taunt'}
            </button>
          )}
        </div>

        {/* Segmented Tabs */}
        <div className="player-profile-tabs">
          <button
            type="button"
            className={`player-profile-tab${activeTab === 'overview' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            📊 {isMn ? 'Статистик' : 'Stats & Record'}
          </button>
          <button
            type="button"
            className={`player-profile-tab${activeTab === 'history' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            📜 {isMn ? 'Тоглолтын түүх' : 'Recent Matches'}
            {p?.recentMatches && p.recentMatches.length > 0 ? ` (${p.recentMatches.length})` : ''}
          </button>
        </div>

        {/* Tab 1: Stats Overview */}
        {activeTab === 'overview' && (
          <div className="player-profile-stats-grid">
            <div className="player-profile-stat-box">
              <span className="stat-label">🎮 {isMn ? 'Нийт тоглосон' : 'Games Played'}</span>
              <span className="stat-value">{gamesPlayed}</span>
            </div>
            <div className="player-profile-stat-box">
              <span className="stat-label">🏆 {isMn ? 'Нийт хожсон' : 'Total Wins'}</span>
              <span className="stat-value" style={{ color: '#4ade80' }}>
                {wins}
              </span>
            </div>
            <div className="player-profile-stat-box">
              <span className="stat-label">📈 {isMn ? 'Хожлын хувь' : 'Win Rate'}</span>
              <span className="stat-value" style={{ color: '#fde047' }}>
                {winRate}%
              </span>
            </div>
            <div className="player-profile-stat-box">
              <span className="stat-label">🍵 {isMn ? 'Түвшин' : 'Tier'}</span>
              <span className="stat-value" style={{ color: isPaid ? '#fbbf24' : '#94a3b8' }}>
                {isPaid ? 'VIP Member' : 'Free Tier'}
              </span>
            </div>
          </div>
        )}

        {/* Tab 2: Overall Public Match History across all rooms */}
        {activeTab === 'history' && (
          <div className="player-profile-history-list">
            {state.inspectingPlayerLoading && (!p || p.recentMatches.length === 0) ? (
              <div className="player-profile-empty-history">
                {isMn ? 'Түүхийг ачааллаж байна...' : 'Loading match records...'}
              </div>
            ) : p?.recentMatches && p.recentMatches.length > 0 ? (
              p.recentMatches.map((match) => (
                <div key={match.id} className="player-profile-match-row">
                  <div className="match-row-left">
                    <span className="match-game-type">{formatGameTypeName(match.gameType)}</span>
                    <span className="match-player-count">
                      {isMn ? `${match.playerCount} тоглогч` : `${match.playerCount} players`}
                    </span>
                  </div>

                  <div className="match-row-right">
                    <span className={`match-badge${match.won ? ' is-win' : ' is-loss'}`}>
                      {match.won ? (isMn ? '🏆 Хожсон' : '🏆 Won') : (isMn ? 'Хожигдсон' : 'Defeat')}
                    </span>
                    <span className="match-time">{formatRelativeTime(match.finishedAt)}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="player-profile-empty-history">
                <span>🎲</span>
                <p>
                  {isMn
                    ? 'Одоогоор нийтийн тоглолтын түүх бүртгэгдээгүй байна.'
                    : 'No public match history recorded yet.'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
